<template>
  <WidgetWrapper
    lg="12"
    :title="$t('delivery.dashboard.deliveryStatus')"
    :tooltip-text="$t('delivery.dashboard.deliveryStatusTooltip')"
  >
    <b-row>
      <b-col md="4" class="d-flex flex-column col-xxl-3">
        <b-skeleton-wrapper :loading="loading" class="w-100">
          <template #loading>
            <b-skeleton-img no-aspect></b-skeleton-img>
          </template>
          <div v-if="showDeliveryOrderChartFilter" class="mb-4">
            <multiselect
              v-model="deliveryOrderChartFilter"
              :options="deliveryOrderChartFilterOptions"
              :multiple="false"
              placeholder="Select one"
              label="label"
            />
          </div>
          <div class="chart-container w-100">
            <McEmptyState
              v-if="totalCount === 0"
              size="sm"
              :desc="$t('delivery.dashboard.noData')"
              @buttonPrimaryAction="handleOpenNewTab"
            />
            <div v-else class="__chart">
              <DoughnutChart class="doughnut-chart" :data="chartData" :options="chartOptions" />
              <div class="total-delivery">
                <div class="__label">{{ $t('global.text.total') }}</div>
                <div class="__number">{{ Number(totalCount).toLocaleString() }}</div>
              </div>
            </div>
          </div>
        </b-skeleton-wrapper>
      </b-col>
      <b-col md="8" class="col-xxl-9">
        <b-row class="g-3">
          <b-col sm="6" class="col-xxl-4 d-flex" v-for="statusKey in statusKeys" :key="statusKey">
            <StatusCard
              :key="statusKey"
              :status="statusKey"
              :data="statusData[statusKey]"
              :loading="loading"
              @click="$emit('showDetail', statusKey)"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </WidgetWrapper>
</template>

<script>
import { mapGetters } from 'vuex'
import Multiselect from 'vue-multiselect'
import McEmptyState from '@/components/McEmptyState'
import DoughnutChart from '@/components/Chart/Doughnut'
import StatusCard from './StatusCard.vue'
import WidgetWrapper from './WidgetWrapper.vue'
import { deliveryStatusKeys } from '../lib/constants/delivery-status-keys'

import * as DeliveryWidget from '@/services/rest-api/lib/dashboard/delivery-widget'

export default {
  components: {
    McEmptyState,
    DoughnutChart,
    StatusCard,
    WidgetWrapper,
    Multiselect,
  },
  emits: ['showDetail', 'deliveryOrderType'],
  data() {
    return {
      loading: false,
      totalCount: 0,

      statusData: {},

      deliveryStatusKeys,
      deliveryOrderChartFilter: {
        key: 'all_delivery',
        label: this.$t('delivery.dashboard.allDelivery'),
      },
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    statusKeys() {
      const vendorStatus =
        this.hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete']) &&
        this.deliveryOrderChartFilter.key !== 'owner_delivery'
          ? ['WAITING_FOR_VENDOR', 'REJECTED_BY_VENDOR', 'ACCEPTED_BY_VENDOR']
          : []
      return ['DRAFT', 'CONFIRMED', ...vendorStatus, 'SCHEDULED', 'STARTED', 'PICKED']
    },
    labels() {
      return this.statusKeys.map((key) => this.$t(`delivery.status.${key.toLowerCase()}`))
    },
    datasets() {
      return [
        {
          backgroundColor: this.statusKeys.map(
            (key) => this.deliveryStatusKeys[key].icon.chartColor
          ),
          data: this.statusKeys.map((key) => this.statusData[key]?.percentage ?? 0),
        },
      ]
    },
    chartData() {
      return { labels: this.labels, datasets: this.datasets }
    },
    chartOptions() {
      return {
        maintainAspectRatio: true,
        responsive: true,
        cutoutPercentage: 55,
        legend: {
          display: false,
        },
        tooltips: {
          backgroundColor: '#344264',
          callbacks: {
            label: function (tooltipItem, data) {
              const index = tooltipItem.index
              const status = data.labels[index]
              const percentage = data.datasets[0].data[index].toFixed(2)

              return `${status}: ${percentage}%`
            },
          },
        },
      }
    },
    deliveryOrderChartFilterOptions() {
      return [
        { key: 'all_delivery', label: this.$t('delivery.dashboard.allDelivery') },
        { key: 'owner_delivery', label: this.$t('delivery.dashboard.selfDelivery') },
        { key: 'vendor_delivery', label: this.$t('delivery.dashboard.vendorDelivery') },
      ]
    },
    showDeliveryOrderChartFilter() {
      return this.hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete'])
    },
  },
  watch: {
    deliveryOrderChartFilter: function (newVal) {
      if (newVal) this.showData(newVal.key)
    },
  },
  mounted() {
    this.showData(this.deliveryOrderChartFilter.key)
  },
  methods: {
    async showData(view) {
      this.loading = true

      const res = await DeliveryWidget.getDeliveryStatusWidget(view)
      if (res.errors) return

      const parsedData = this.parseData(res.data.status)
      this.statusData = parsedData

      this.totalCount = res.data.total_count
      this.$emit('deliveryOrderType', view)

      this.loading = false
    },
    // TODO: Delete this method when BE passed all this widget keys
    parseData(data) {
      const parsedData = {}

      this.statusKeys.forEach((key) => {
        let obj = {}
        if (data[key]) {
          obj = Object.assign({}, data[key])
        }

        parsedData[key] = obj
      })

      return parsedData
    },
    handleOpenNewTab() {
      const route = this.$router.resolve({ name: 'Delivery Create' })
      window.open(route.href, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
.chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.__chart {
  position: relative;
  display: flex;
  justify-content: center;

  .doughnut-chart {
    height: 200px;
    width: 200px;
  }

  .total-delivery {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;

    .__label {
      color: $teal-400;
    }

    .__number {
      font-size: 22px;
      font-weight: 600;
    }
  }
}
</style>
